import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import axios from "axios";
import PassportAnalysisHistory from "../components/learner/PassportAnalysisHistory";
import ProofHistoryPanel from "../components/learner/ProofHistoryPanel";
import CalmEmptyState from "../components/learner/CalmEmptyState";
import useConsumeSearchParam from "../hooks/useConsumeSearchParam";
import flashFocusElement from "../hooks/flashFocusElement";
import {
  ShieldCheck, ArrowLeft, Copy, Loader2, Check, X,
  BadgeCheck, AlertTriangle, Lock, Share2, Clock, Sparkles, FileText,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useTruthState, TRUTH_STATE_NEEDS_INPUT } from "../hooks/useTruthState";
import { TruthReceipt } from "../components/TruthReceipt";
import { formatApiError } from "../utils/apiError";

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_CONSENT = {
  share_homework: true,
  share_theory_progress: true,
  share_self_assessment: true,
  share_confidence_logs: true,
  share_learner_notes: false,
  share_goals: true,
  share_engagement: true,
  share_owned_videos: false,
};

const CONSENT_LABELS = {
  share_homework: "Homework history",
  share_theory_progress: "Theory progress",
  share_self_assessment: "Self-assessment data",
  share_confidence_logs: "Confidence check-ins",
  share_learner_notes: "Your private notes",
  share_goals: "Learning goals",
  share_engagement: "Engagement stats",
  share_owned_videos: "Owned lesson videos",
};

export default function LearnerPassport() {
  const nav = useNavigate();
  const goBack = useSafeBack();
  const [searchParams, setSearchParams] = useSearchParams();
  const [passport, setPassport] = useState(null);
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);
  const [form, setForm] = useState({
    transmission: "manual",
    target_area: "",
    ...DEFAULT_CONSENT,
  });
  const [creating, setCreating] = useState(false);
  const [upgradingId, setUpgradingId] = useState("");
  const [generatingId, setGeneratingId] = useState("");
  const [packTokens, setPackTokens] = useState({});
  const [progressView, setProgressView] = useState(null);
  // Batch P1 · Passport Truth-State — single canonical async receipt
  // for every Passport-domain action (load, checkout, generate pack,
  // create share, revoke share, copy link). Bespoke `toast.success` /
  // `toast.error` calls have been removed in favour of <TruthReceipt />.
  const truth = useTruthState();

  const load = useCallback(async () => {
    try {
      await truth.run(
        async () => {
          const [p, s, pg] = await Promise.all([
            axios.get(`${API}/api/passport/me`, { withCredentials: true }),
            axios.get(`${API}/api/passport/shares/my`, { withCredentials: true }),
            axios.get(`${API}/api/passport/progress`, { withCredentials: true })
              .catch(() => ({ data: null })),
          ]);
          setPassport(p.data);
          setShares(s.data.items || []);
          setProgressView(pg.data || null);
        },
        { failedMessage: "Unable to load passport." },
      );
    } catch { /* truth.run captured; TruthReceipt shows Retry */ }
    setLoading(false);
  }, [truth]);

  useEffect(() => { load(); }, [load]);

  // Operational Continuity Convergence V1.1 (Feb 28, 2026) —
  // consume `?clip={id}` from the `ai_analysis_ready` bell
  // notification (passport_phase3.py) and gently scroll-to + flash
  // the matching row inside <PassportAnalysisHistory />. The
  // history component's <li> exposes `id="row-clip-${entry.source_id}"`
  // for this purpose. Param drops via {replace:true} so the flash
  // doesn't re-fire on history navigation. Safe no-op if the entry
  // hasn't yet propagated.
  const focusClipId = useConsumeSearchParam("clip");
  useEffect(() => {
    if (!focusClipId) return;
    const t = window.setTimeout(
      () => flashFocusElement(`row-clip-${focusClipId}`),
      500,
    );
    return () => window.clearTimeout(t);
  }, [focusClipId]);

  // After returning from Stripe Checkout (pay=success&session_id=…), poll the
  // status endpoint until the share tier flips to verified_progress_pack, then
  // refresh and clear the query params.
  useEffect(() => {
    const pay = searchParams.get("pay");
    const sessionId = searchParams.get("session_id");
    if (pay === "cancel") {
      truth.setState(TRUTH_STATE_NEEDS_INPUT, "Payment cancelled — try again any time.");
      setSearchParams({}, { replace: true });
      return;
    }
    if (pay !== "success" || !sessionId) return;

    let attempts = 0;
    let cancelled = false;
    const tick = async () => {
      if (cancelled || attempts >= 8) {
        if (!cancelled) {
          truth.setState("waiting", "Payment processing — we'll refresh once Stripe confirms.");
          setSearchParams({}, { replace: true });
          await load();
        }
        return;
      }
      attempts += 1;
      try {
        const { data } = await axios.get(
          `${API}/api/passport/pay/status/${sessionId}`,
          { withCredentials: true },
        );
        if (data?.payment_status === "paid") {
          truth.setState("completed", "Upgrade complete — pack ready to generate.");
          setSearchParams({}, { replace: true });
          await load();
          return;
        }
      } catch {
        /* swallow — keep polling */
      }
      setTimeout(tick, 1500);
    };
    tick();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const upgradeToPack = async (share) => {
    setUpgradingId(share.id);
    try {
      await truth.run(
        async () => {
          const { data } = await axios.post(
            `${API}/api/passport/pay/checkout`,
            {
              product: "verified_progress_pack",
              share_id: share.id,
              origin_url: window.location.origin,
            },
            { withCredentials: true },
          );
          if (data?.founder_mode || data?.no_charge) {
            // Founder admin bypass — surface as `needs_input` so it
            // doesn't collide with the completed contract; the copy
            // is authoritative from the backend.
            truth.setState(
              TRUTH_STATE_NEEDS_INPUT,
              data.message || "Admin billing bypass — use a real test account.",
            );
            return data;
          }
          if (data?.url) {
            window.location.href = data.url;
            return data;
          }
          throw new Error("Unable to start checkout");
        },
        { failedMessage: "Unable to start checkout." },
      );
    } catch (err) {
      // Escalate axios-error detail into the receipt for parity with
      // the previous formatApiError copy.
      const detail = formatApiError(err, "Unable to start checkout.");
      truth.setState("failed", detail);
    } finally {
      setUpgradingId("");
    }
  };

  const generatePack = async (share) => {
    setGeneratingId(share.id);
    try {
      await truth.run(
        async () => {
          const { data } = await axios.post(
            `${API}/api/passport/pack/${share.id}/generate`,
            {},
            { withCredentials: true },
          );
          if (!data?.token) throw new Error("Pack generation failed");
          setPackTokens((t) => ({ ...t, [share.id]: data.token }));
          nav(`/pack/view/${data.token}`);
          return data;
        },
        {
          completedMessage: "Verified Progress Pack generated.",
          failedMessage:    "Unable to generate pack.",
        },
      );
    } catch { /* truth.run captured */ }
    setGeneratingId("");
  };

  const createShare = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await truth.run(
        async () => {
          await axios.post(
            `${API}/api/passport/shares`,
            { tier: "basic_transfer", ...form },
            { withCredentials: true }
          );
          setOpening(false);
          setForm({ transmission: "manual", target_area: "", ...DEFAULT_CONSENT });
          await load();
        },
        {
          completedMessage: "Basic Transfer Data share created.",
          failedMessage:    "Unable to create share.",
        },
      );
    } catch { /* truth.run captured */ }
    setCreating(false);
  };

  const revokeShare = async (id) => {
    if (!window.confirm("Revoke this share? Instructors will no longer be able to view it.")) return;
    try {
      await truth.run(
        async () => {
          await axios.post(
            `${API}/api/passport/shares/${id}/revoke`,
            { reason: "Learner revoked from UI" },
            { withCredentials: true }
          );
          await load();
        },
        {
          completedMessage: "Share revoked.",
          failedMessage:    "Unable to revoke share.",
        },
      );
    } catch { /* truth.run captured */ }
  };

  const copyLink = (token) => {
    const link = `${window.location.origin}/p/share/${token}`;
    navigator.clipboard.writeText(link);
    truth.setState("completed", "Share link copied.");
  };

  // Batch P1 · Passport Truth-State — calm auto-clear for terminal
  // successes. Failed / needs_input / waiting states linger until
  // the user dismisses or retries.
  useEffect(() => {
    if (truth.state === "completed") {
      const t = setTimeout(() => truth.setState(null), 1200);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [truth]);

  if (loading) {
    return (
      <div className="min-h-dvh no-overflow-x flex items-center justify-center bg-slate-50 dark:bg-[#0c1120]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const pct = passport?.engagement?.theory_progress?.percent || 0;
  const level = passport?.level_estimate?.label || "Early Stage";

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-24">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-30">
        <div className="max-w-lg mx-auto px-4 h-14 flex items-center gap-3">
          <button data-testid="passport-back-btn" onClick={() => goBack()} className="text-slate-400 hover:text-slate-600">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <ShieldCheck className="w-5 h-5 text-blue-600" />
          <p className="font-extrabold text-slate-900 dark:text-white text-sm flex-1">Your Passport</p>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 pt-4 space-y-4">
        {/* Batch P1 · Passport Truth-State — canonical async receipt.
            Docked at the top of the main column so every Passport
            action's outcome lands consistently. Failed states offer
            Retry + Dismiss (never a dead end). */}
        <TruthReceipt
          state={truth.state}
          message={truth.message}
          onRetry={load}
          onDismiss={() => truth.setState(null)}
        />

        {/* Structured Profile CTA */}
        <button
          data-testid="passport-profile-link"
          onClick={() => nav("/passport/profile")}
          className="w-full rounded-2xl bg-gradient-to-br from-indigo-600 to-blue-600 text-white p-4 text-left active:scale-[0.99] transition-all flex items-center gap-3 shadow-md"
        >
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M12 4v16m8-8H4"/></svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-extrabold">Your Driving Passport · 7 sections</p>
            <p className="text-[11px] text-white/90">Identity · Availability · Readiness · Progress · Style · Notes · Transfer view</p>
          </div>
          <svg className="w-5 h-5 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7"/></svg>
        </button>

        {/* Ownership banner */}
        <div
          data-testid="passport-ownership-notice"
          className="rounded-2xl border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50 dark:bg-emerald-900/20 p-4 flex gap-3"
        >
          <BadgeCheck className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-emerald-900 dark:text-emerald-200">Your learner-owned data is yours.</p>
            <p className="text-xs text-emerald-800/80 dark:text-emerald-300/80 mt-1">
              Premium AI evidence packs are optional. Instructor review reports are guidance only.
            </p>
          </div>
        </div>

        {/* AI Analysis history — permanent record of paid AI reviews */}
        <PassportAnalysisHistory />

        {/* FOS-8 · Learner progress view — safe wording, no score, no rank */}
        {progressView && Array.isArray(progressView.areas)
          && progressView.areas.length > 0 && (
          <section
            data-testid="passport-progress-view"
            className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 space-y-3"
          >
            <div className="flex items-center justify-between">
              <h2
                data-testid="passport-progress-title"
                className="text-lg font-semibold text-slate-900 dark:text-slate-100"
              >
                Your progress
              </h2>
              <span className="text-[10px] text-slate-500 uppercase tracking-wider">
                Reflection, not a score
              </span>
            </div>
            <ul className="space-y-2">
              {progressView.areas.map((area, i) => (
                <li
                  key={`${area.area}-${i}`}
                  data-testid={`passport-progress-area-${area.area.replace(/\s+/g, "-")}`}
                  className="rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700 p-3"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-sm font-medium text-slate-800 dark:text-slate-100 capitalize">
                      {area.area}
                    </span>
                    <span
                      data-testid={`passport-progress-label-${area.area.replace(/\s+/g, "-")}`}
                      className="text-[12px] font-semibold text-emerald-700 dark:text-emerald-300 uppercase tracking-wider"
                    >
                      {area.label}
                    </span>
                  </div>
                  {area.story && (
                    <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 leading-snug">
                      {area.story}
                    </p>
                  )}
                </li>
              ))}
            </ul>
            {progressView.guidance && (
              <p
                data-testid="passport-progress-guidance"
                className="text-[11px] text-slate-500 dark:text-slate-400 italic pt-1"
              >
                {progressView.guidance}
              </p>
            )}
          </section>
        )}

        {/* Passport snapshot */}
        <section
          data-testid="passport-snapshot"
          className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5"
        >
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs uppercase tracking-wide text-slate-500 font-bold">Current level (guidance only)</p>
            <span
              data-testid="passport-level"
              className="text-[11px] font-bold px-2 py-1 rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900"
            >
              {level}
            </span>
          </div>
          <div className="space-y-3 mt-4">
            <StatRow label="Theory progress" value={`${pct}%`} sub={`${passport.engagement.theory_progress.completed}/${passport.engagement.theory_progress.total} lessons`} />
            <StatRow
              label="Confidence check-ins"
              value={passport.engagement.confidence.checks_completed}
              sub={passport.engagement.confidence.average_score != null ? `avg ${passport.engagement.confidence.average_score}%` : "no checks yet"}
            />
            <StatRow
              label="Self-assessment"
              value={passport.engagement.self_assessment?.level_name || "Not done"}
              sub={passport.engagement.self_assessment?.strength || "–"}
            />
            <StatRow label="Completed lessons" value={passport.lessons.total_completed} />
          </div>
          <p className="text-[11px] text-slate-400 mt-4 italic">
            {passport.level_estimate.disclaimer}
          </p>
        </section>

        {/* Proof history (Feb 13, 2026 — Independent Proof Learning).
            Renders only if the learner has any proof ledger rows. */}
        <ProofHistoryPanel />

        <section className="border-t border-slate-200 dark:border-slate-700 pt-4" data-testid="passport-learning-follow-up">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Continue your questions, homework and instructor follow-ups in your learning to-do list. Answers are kept for review; they do not confirm driving competence.
          </p>
          <button type="button" onClick={() => nav("/learn/learning")} className="min-h-11 underline text-emerald-700 dark:text-emerald-300">
            Open my learning to-do list
          </button>
        </section>

        {/* Create share */}
        <section className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-extrabold text-slate-900 dark:text-white">Share with an instructor</p>
            <Share2 className="w-4 h-4 text-slate-400" />
          </div>
          <p className="text-xs text-slate-500 mb-4">
            Free Basic Transfer Data only shares the information you choose below.
          </p>

          {!opening ? (
            <Button data-testid="passport-new-share-btn" onClick={() => setOpening(true)} className="w-full h-11 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm">
              New Basic Transfer Data share (free)
            </Button>
          ) : (
            <form onSubmit={createShare} className="space-y-3 mt-1" data-testid="passport-share-form">
              <div>
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Target area</label>
                <input
                  data-testid="passport-target-area"
                  value={form.target_area}
                  onChange={(e) => setForm((f) => ({ ...f, target_area: e.target.value }))}
                  placeholder="e.g. Leeds LS6"
                  className="w-full mt-1 h-10 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Transmission</label>
                <select
                  data-testid="passport-transmission"
                  value={form.transmission}
                  onChange={(e) => setForm((f) => ({ ...f, transmission: e.target.value }))}
                  className="w-full mt-1 h-10 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 text-sm"
                >
                  <option value="manual">Manual</option>
                  <option value="auto">Automatic</option>
                  <option value="either">Either</option>
                </select>
              </div>

              <div className="pt-2">
                <p className="text-xs font-bold text-slate-700 dark:text-slate-300 mb-2">Choose what to share:</p>
                <div className="space-y-2">
                  {Object.entries(CONSENT_LABELS).map(([k, label]) => (
                    <label
                      key={k}
                      data-testid={`consent-${k}`}
                      className="flex items-center gap-3 text-sm text-slate-700 dark:text-slate-200 cursor-pointer select-none"
                    >
                      <input
                        type="checkbox"
                        checked={!!form[k]}
                        onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.checked }))}
                        className="w-4 h-4 rounded border-slate-300 text-slate-900"
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  data-testid="passport-share-cancel"
                  onClick={() => setOpening(false)}
                  variant="outline"
                  className="flex-1 h-10 rounded-xl"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  data-testid="passport-share-submit"
                  disabled={creating}
                  className="flex-1 h-10 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm"
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create share"}
                </Button>
              </div>
            </form>
          )}
        </section>

        {/* Existing shares */}
        <section className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5" data-testid="passport-shares-list">
          <p className="text-sm font-extrabold text-slate-900 dark:text-white mb-1">Your shares</p>
          <p className="text-xs text-slate-500 mb-4">Revoke any share instantly — instructors lose access.</p>
          {shares.length === 0 ? (
            <CalmEmptyState
              testid="passport-shares-empty"
              tone="nothing-yet"
              title="No shares yet"
              body="Create a Basic Transfer Data share above to safely move what you've learned to a new instructor."
            />
          ) : (
            <div className="space-y-3">
              {shares.map((s) => (
                <div
                  key={s.id}
                  data-testid={`share-row-${s.id}`}
                  className={`rounded-xl border p-3 ${s.status === "active" ? "border-slate-200 dark:border-slate-700" : "border-slate-200 dark:border-slate-700 opacity-60"}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200">
                      {s.tier_label}
                    </span>
                    <span
                      className={`text-[10px] font-bold px-2 py-1 rounded-full ${
                        s.status === "active"
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {s.status}
                    </span>
                    {s.expires_at && (
                      <span className="text-[10px] text-slate-400 flex items-center gap-1 ml-auto">
                        <Clock className="w-3 h-3" /> expires {new Date(s.expires_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mb-2">
                    {s.target_area ? `${s.target_area} · ` : ""}
                    {s.transmission ? s.transmission : "any transmission"}
                  </p>
                  {s.status === "active" && (
                    <>
                      <div className="flex gap-2">
                        <Button
                          data-testid={`share-copy-${s.id}`}
                          onClick={() => copyLink(s.token)}
                          size="sm"
                          variant="outline"
                          className="h-8 rounded-lg text-xs"
                        >
                          <Copy className="w-3 h-3 mr-1" /> Copy link
                        </Button>
                        <Button
                          data-testid={`share-revoke-${s.id}`}
                          onClick={() => revokeShare(s.id)}
                          size="sm"
                          variant="outline"
                          className="h-8 rounded-lg text-xs text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <X className="w-3 h-3 mr-1" /> Revoke
                        </Button>
                      </div>

                      {/* Verified Progress Pack — upgrade / generate / view */}
                      <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                        {s.tier === "basic_transfer" && (
                          <Button
                            data-testid={`share-upgrade-${s.id}`}
                            onClick={() => upgradeToPack(s)}
                            disabled={upgradingId === s.id}
                            size="sm"
                            className="w-full h-9 rounded-lg text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-bold"
                          >
                            {upgradingId === s.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <>
                                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                                Upgrade to Verified Pack (£15)
                              </>
                            )}
                          </Button>
                        )}
                        {s.tier === "verified_progress_pack" && !packTokens[s.id] && (
                          <Button
                            data-testid={`share-generate-${s.id}`}
                            onClick={() => generatePack(s)}
                            disabled={generatingId === s.id}
                            size="sm"
                            className="w-full h-9 rounded-lg text-xs bg-slate-900 hover:bg-slate-800 text-white font-bold"
                          >
                            {generatingId === s.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <>
                                <FileText className="w-3.5 h-3.5 mr-1.5" />
                                Generate Verified Progress Pack
                              </>
                            )}
                          </Button>
                        )}
                        {s.tier === "verified_progress_pack" && packTokens[s.id] && (
                          <Button
                            data-testid={`share-view-pack-${s.id}`}
                            onClick={() => nav(`/pack/view/${packTokens[s.id]}`)}
                            size="sm"
                            variant="outline"
                            className="w-full h-9 rounded-lg text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                          >
                            <BadgeCheck className="w-3.5 h-3.5 mr-1.5" /> View Pack
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Guidance only notice */}
        <div
          data-testid="passport-guidance-notice"
          className="rounded-2xl border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/20 p-4 flex gap-3"
        >
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-[12px] text-amber-900 dark:text-amber-200 leading-relaxed">
            Level estimates and shared data are decision-support guidance only — not a pass prediction or readiness guarantee.
          </p>
        </div>
      </main>
    </div>
  );
}

function StatRow({ label, value, sub }) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-bold text-slate-900 dark:text-white">{label}</p>
        {sub && <p className="text-[11px] text-slate-400">{sub}</p>}
      </div>
      <p className="text-sm font-extrabold text-slate-900 dark:text-white">{value}</p>
    </div>
  );
}
